import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';

// ============================================================
// Fermi Education Platform - Modern Theme
// ============================================================
// Palette inspired by professional education platforms.
// Component overrides transform ALL MUI components globally.
// Business logic is NEVER affected - only visual presentation.
// ============================================================

const PRIMARY = '#1565C0';     // Strong trustworthy blue
const PRIMARY_DARK = '#0D47A1';
const PRIMARY_LIGHT = '#42A5F5';
const SECONDARY = '#00897B';   // Teal - growth/learning
const SECONDARY_DARK = '#00695C';
const SECONDARY_LIGHT = '#4DB6AC';

const BG_DEFAULT = '#F0F2F5';
const BG_PAPER = '#FFFFFF';

const TEXT_PRIMARY = '#1A2027';
const TEXT_SECONDARY = '#637381';

const SUCCESS = '#2E7D32';
const WARNING = '#ED6C02';
const ERROR = '#D32F2F';
const INFO = '#0288D1';

const SIDEBAR_BG = '#0D1B2A';
const SIDEBAR_TEXT = '#B0BEC5';
const SIDEBAR_ACTIVE = PRIMARY_LIGHT;
const TOPBAR_BG = PRIMARY_DARK;

const RADIUS = 10;
const RADIUS_SM = 6;

let fermiTheme = createTheme({
  palette: {
    primary: {
      main: PRIMARY,
      dark: PRIMARY_DARK,
      light: PRIMARY_LIGHT,
      contrastText: '#fff',
    },
    secondary: {
      main: SECONDARY,
      dark: SECONDARY_DARK,
      light: SECONDARY_LIGHT,
      contrastText: '#fff',
    },
    success: { main: SUCCESS },
    warning: { main: WARNING },
    error: { main: ERROR },
    info: { main: INFO },
    background: {
      default: BG_DEFAULT,
      paper: BG_PAPER,
    },
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
    },
    divider: alpha('#919EAB', 0.2),
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.3 },
    h3: { fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.3 },
    h4: { fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.4 },
    h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.5 },
    subtitle1: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.85rem', lineHeight: 1.6 },
    button: { fontWeight: 600, textTransform: 'none' as const, fontSize: '0.875rem' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, color: TEXT_SECONDARY },
    overline: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em' },
  },

  shape: {
    borderRadius: RADIUS,
  },

  shadows: [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.05)',                                  // 1
    '0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px -1px rgba(0,0,0,0.04)', // 2
    '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.04)', // 3
    '0 6px 12px -2px rgba(0,0,0,0.08), 0 3px 7px -3px rgba(0,0,0,0.05)', // 4
    '0 8px 16px -3px rgba(0,0,0,0.08), 0 4px 8px -4px rgba(0,0,0,0.04)', // 5
    '0 12px 24px -4px rgba(0,0,0,0.10), 0 6px 12px -6px rgba(0,0,0,0.04)', // 6
    '0 14px 28px -5px rgba(0,0,0,0.10), 0 8px 16px -8px rgba(0,0,0,0.04)', // 7
    '0 16px 32px -5px rgba(0,0,0,0.10), 0 8px 16px -8px rgba(0,0,0,0.06)', // 8
    '0 20px 36px -6px rgba(0,0,0,0.12), 0 10px 20px -10px rgba(0,0,0,0.06)', // 9
    '0 24px 40px -8px rgba(0,0,0,0.12), 0 12px 24px -12px rgba(0,0,0,0.06)', // 10
    '0 28px 44px -8px rgba(0,0,0,0.14), 0 14px 28px -14px rgba(0,0,0,0.06)', // 11
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 12
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 13
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 14
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 15
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 16
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 17
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 18
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 19
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 20
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 21
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 22
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 23
    '0 32px 48px -8px rgba(0,0,0,0.14), 0 16px 32px -16px rgba(0,0,0,0.06)', // 24
  ],

  components: {
    // ──── GLOBAL ────
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          background: alpha('#919EAB', 0.3),
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: alpha('#919EAB', 0.5),
        },
      },
    },

    // ──── APP BAR ────
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: TOPBAR_BG,
          backgroundImage: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 100%)`,
          boxShadow: '0 2px 12px 0 rgba(0,0,0,0.15)',
        },
      },
    },

    // ──── PAPER / CARDS ────
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          backgroundImage: 'none',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px -1px rgba(0,0,0,0.04)',
        },
        elevation2: {
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.04)',
        },
        elevation3: {
          boxShadow: '0 6px 16px -4px rgba(0,0,0,0.10), 0 3px 7px -3px rgba(0,0,0,0.05)',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.06)',
          border: `1px solid ${alpha('#919EAB', 0.12)}`,
          transition: 'box-shadow 0.25s ease, transform 0.25s ease',
          '&:hover': {
            boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)',
          },
        },
      },
    },

    // ──── BUTTONS ────
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          padding: '8px 20px',
          transition: 'all 0.2s ease',
          fontWeight: 600,
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, ${PRIMARY} 100%)`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: alpha(PRIMARY, 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(PRIMARY, 0.06),
          },
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '0.8rem',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(PRIMARY, 0.08),
          },
        },
      },
    },

    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 14px 0 rgba(0,0,0,0.15)',
        },
      },
    },

    // ──── INPUTS ────
    MuiTextField: {
      defaultProps: {
        variant: 'outlined' as const,
        size: 'small' as const,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: RADIUS_SM,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: PRIMARY_LIGHT,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: PRIMARY_LIGHT,
          },
        },
        notchedOutline: {
          borderColor: alpha('#919EAB', 0.3),
          transition: 'border-color 0.2s ease',
        },
      },
    },

    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: `${RADIUS_SM}px ${RADIUS_SM}px 0 0`,
          backgroundColor: alpha('#919EAB', 0.06),
          '&:hover': {
            backgroundColor: alpha('#919EAB', 0.1),
          },
          '&.Mui-focused': {
            backgroundColor: alpha('#919EAB', 0.06),
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          '&.Mui-focused': {
            color: PRIMARY,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: RADIUS_SM,
        },
      },
    },

    // ──── TABLES ────
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          overflow: 'hidden',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: alpha(PRIMARY, 0.06),
            color: TEXT_PRIMARY,
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.05em',
            borderBottom: `2px solid ${alpha(PRIMARY, 0.12)}`,
            padding: '12px 16px',
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background-color 0.15s ease',
            '&:nth-of-type(even)': {
              backgroundColor: alpha('#919EAB', 0.03),
            },
            '&:hover': {
              backgroundColor: alpha(PRIMARY, 0.04),
            },
            '&:last-of-type td': {
              borderBottom: 0,
            },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha('#919EAB', 0.12)}`,
          padding: '10px 16px',
          fontSize: '0.85rem',
        },
      },
    },

    // ──── DIALOGS ────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS * 1.2,
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.15rem',
          fontWeight: 700,
          padding: '20px 24px 12px',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: '8px',
        },
      },
    },

    // ──── DRAWER ────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: SIDEBAR_BG,
          color: SIDEBAR_TEXT,
          borderRight: 'none',
          boxShadow: '2px 0 16px rgba(0,0,0,0.1)',
        },
      },
    },

    // ──── TABS ────
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 44,
          borderRadius: `${RADIUS_SM}px ${RADIUS_SM}px 0 0`,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(PRIMARY, 0.06),
            color: PRIMARY,
          },
          '&.Mui-selected': {
            color: PRIMARY,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: PRIMARY,
        },
      },
    },

    // ──── CHIPS ────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          fontWeight: 500,
          fontSize: '0.8rem',
        },
        colorPrimary: {
          backgroundColor: alpha(PRIMARY, 0.1),
          color: PRIMARY_DARK,
        },
        colorSecondary: {
          backgroundColor: alpha(SECONDARY, 0.1),
          color: SECONDARY_DARK,
        },
        colorSuccess: {
          backgroundColor: alpha(SUCCESS, 0.1),
          color: SUCCESS,
        },
        colorWarning: {
          backgroundColor: alpha(WARNING, 0.1),
          color: WARNING,
        },
        colorError: {
          backgroundColor: alpha(ERROR, 0.1),
          color: ERROR,
        },
      },
    },

    // ──── TOOLTIP ────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A2027',
          borderRadius: RADIUS_SM,
          fontSize: '0.75rem',
          padding: '6px 12px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        },
        arrow: {
          color: '#1A2027',
        },
      },
    },

    // ──── SNACKBAR ────
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          fontSize: '0.85rem',
          fontWeight: 500,
        },
        standardSuccess: {
          backgroundColor: alpha(SUCCESS, 0.1),
          color: SUCCESS,
        },
        standardError: {
          backgroundColor: alpha(ERROR, 0.1),
          color: ERROR,
        },
        standardWarning: {
          backgroundColor: alpha(WARNING, 0.1),
          color: WARNING,
        },
        standardInfo: {
          backgroundColor: alpha(INFO, 0.1),
          color: INFO,
        },
      },
    },

    // ──── LIST ────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          margin: '2px 8px',
          padding: '8px 12px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(SIDEBAR_ACTIVE, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(SIDEBAR_ACTIVE, 0.12),
            color: SIDEBAR_ACTIVE,
            '&:hover': {
              backgroundColor: alpha(SIDEBAR_ACTIVE, 0.16),
            },
            '& .MuiListItemIcon-root': {
              color: SIDEBAR_ACTIVE,
            },
          },
        },
      },
    },

    // ──── BADGE ────
    MuiBadge: {
      styleOverrides: {
        standard: {
          fontWeight: 700,
          fontSize: '0.7rem',
        },
      },
    },

    // ──── AVATAR ────
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.9rem',
        },
        colorDefault: {
          backgroundColor: alpha(PRIMARY, 0.12),
          color: PRIMARY,
        },
      },
    },

    // ──── BREADCRUMBS ────
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
        },
        separator: {
          color: TEXT_SECONDARY,
        },
      },
    },

    // ──── CIRCULAR PROGRESS ────
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: PRIMARY,
        },
      },
    },

    // ──── LINEAR PROGRESS ────
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: alpha(PRIMARY, 0.1),
        },
        bar: {
          borderRadius: 4,
        },
      },
    },

    // ──── ACCORDION ────
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: `${RADIUS}px !important`,
          border: `1px solid ${alpha('#919EAB', 0.12)}`,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },

    // ──── SKELETON ────
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
        },
      },
    },

    // ──── MENU ────
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: RADIUS,
          boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12)',
          border: `1px solid ${alpha('#919EAB', 0.1)}`,
          marginTop: 4,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: RADIUS_SM,
          margin: '2px 6px',
          padding: '8px 12px',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(PRIMARY, 0.06),
          },
        },
      },
    },

    // ──── DIVIDER ────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha('#919EAB', 0.15),
        },
      },
    },

    // ──── SWITCH ────
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
      },
    },

    // ──── PAGINATION ────
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          fontWeight: 500,
          '&.Mui-selected': {
            fontWeight: 700,
          },
        },
      },
    },
  },
});

fermiTheme = responsiveFontSizes(fermiTheme);

// Export constants for use in components that need direct access
export const themeConstants = {
  SIDEBAR_BG,
  SIDEBAR_TEXT,
  SIDEBAR_ACTIVE,
  TOPBAR_BG,
  PRIMARY,
  PRIMARY_DARK,
  PRIMARY_LIGHT,
  SECONDARY,
  RADIUS,
  RADIUS_SM,
};

export default fermiTheme;
